import { useState } from "react";
import { useForm } from "react-hook-form";
import { Check, Copy, RefreshCw, ShieldCheck } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FormModal,
  Input,
  Select,
  Tooltip,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { generateSecurePassword } from "@marekl/adminer/util";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import type { Role, User } from "../../auth/queries";
import {
  useCreateUser,
  useUpdateUser,
  type UserCreate,
} from "../../lib/queries/users";

export function RoleBadge({ role }: { role: Role }) {
  return role === "admin" ? (
    <Badge variant="err">Administrator</Badge>
  ) : (
    <Badge variant="info">Viewer</Badge>
  );
}

/**
 * Small copy-to-clipboard affordance for a revealed generated password —
 * same styling and Copied feedback as TokenRevealModal's copy button.
 */
function CopyPasswordButton({ getValue }: { getValue: () => string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    const value = getValue();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Tooltip content="Copy the password to the clipboard">
      <Button
        type="button"
        size="sm"
        variant="primary"
        weight="outline"
        onClick={copy}
        startIcon={
          copied ? (
            <Check size={14} aria-hidden="true" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )
        }
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </Tooltip>
  );
}

function GeneratePasswordButton({ onGenerate }: { onGenerate: () => void }) {
  return (
    <Tooltip content="Generate a secure password">
      <Button
        type="button"
        size="sm"
        variant="primary"
        weight="soft"
        startIcon={<RefreshCw size={14} aria-hidden="true" />}
        onClick={onGenerate}
      >
        Generate
      </Button>
    </Tooltip>
  );
}

export function CreateUserModal({ onClose }: { onClose: () => void }) {
  const createUser = useCreateUser();
  // Masked while typed by hand; generating reveals the value so it can be
  // copied and handed to the new user.
  const [revealed, setRevealed] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm<UserCreate>({
    mode: "onTouched",
    defaultValues: { username: "", password: "", role: "viewer" },
  });

  async function onSubmit(values: UserCreate) {
    try {
      await createUser.mutateAsync({
        ...values,
        username: values.username.trim(),
      });
      notifySuccess("The user was created.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<UserCreate>(err, setError, {
        fallbackMessage: "Creation failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="Add user"
      onClose={onClose}
      busy={createUser.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Create"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Username"
        required
        autoComplete="off"
        error={errors.username?.message}
        {...register("username", {
          required: "Required field",
          validate: (v) => (v.trim().length > 0 ? true : "Required field"),
        })}
      />
      <Select
        label="Role"
        prefix={<ShieldCheck size={14} aria-hidden="true" />}
        hint="A viewer sees everything but changes nothing."
        {...register("role")}
      >
        <option value="viewer">Viewer</option>
        <option value="admin">Administrator</option>
      </Select>
      <Input
        label="Password"
        required
        type={revealed ? "text" : "password"}
        autoComplete="new-password"
        spellCheck={false}
        error={errors.password?.message}
        {...register("password", {
          required: "Required field",
          minLength: { value: 10, message: "The password must be at least 10 characters" },
        })}
        suffix={
          <span style={{ display: "inline-flex", gap: 6 }}>
            {revealed && (
              <CopyPasswordButton getValue={() => getValues("password")} />
            )}
            <GeneratePasswordButton
              onGenerate={() => {
                setValue("password", generateSecurePassword(), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setRevealed(true);
              }}
            />
          </span>
        }
      />
    </FormModal>
  );
}

export function EditUserModal({
  user,
  isSelf,
  onClose,
}: {
  user: User;
  isSelf: boolean;
  onClose: () => void;
}) {
  const updateUser = useUpdateUser();
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState("");
  // Masked while typed by hand; generating reveals the value so it can be
  // copied and handed to the user.
  const [revealed, setRevealed] = useState(false);

  async function onSubmit() {
    if (password && password.length < 10) {
      setPwError("The password must be at least 10 characters");
      return;
    }
    try {
      await updateUser.mutateAsync({
        id: user.id,
        input: {
          role,
          active,
          // Empty = keep the current password.
          ...(password ? { password } : {}),
        },
      });
      notifySuccess("The user was updated.");
      onClose();
    } catch {
      // Shown by the FormModal error slot (updateUser suppresses the toast).
    }
  }

  return (
    <FormModal
      open
      title={`Edit: ${user.username}`}
      onClose={onClose}
      busy={updateUser.isPending}
      error={updateUser.error ?? undefined}
      onSubmit={() => void onSubmit()}
    >
      {isSelf && (
        <Alert variant="info">
          You are editing your own account — the role and active flag cannot be changed.
        </Alert>
      )}
      <Select
        label="Role"
        id="edit-user-role"
        value={role}
        disabled={isSelf}
        onChange={(e) => setRole(e.target.value as Role)}
      >
        <option value="viewer">Viewer</option>
        <option value="admin">Administrator</option>
      </Select>
      <Checkbox
        label="Active"
        checked={active}
        disabled={isSelf}
        onChange={(e) => setActive(e.target.checked)}
      />
      <Input
        id="edit-user-password"
        label="New password"
        type={revealed ? "text" : "password"}
        value={password}
        autoComplete="new-password"
        spellCheck={false}
        placeholder="leave empty to keep current"
        hint="Fill in only to reset the user’s password."
        error={pwError}
        onChange={(e) => {
          setPassword(e.target.value);
          setPwError("");
        }}
        suffix={
          <span style={{ display: "inline-flex", gap: 6 }}>
            {revealed && <CopyPasswordButton getValue={() => password} />}
            <GeneratePasswordButton
              onGenerate={() => {
                setPassword(generateSecurePassword());
                setPwError("");
                setRevealed(true);
              }}
            />
          </span>
        }
      />
    </FormModal>
  );
}
